"use client";

import type { ReactNode } from "react";

import { useLayoutEffect, useRef } from "react";

type EntranceOnViewProps = {
  children: ReactNode;
  className?: string;
};

/**
 * A zero-size box has no visible pixels, so it is not treated as already in
 * view. Otherwise a node that has not been laid out skips the scroll-in hold.
 */
function isInViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return false;
  }

  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

/**
 * Marks the wrapper the first time it intersects the viewport.
 *
 * Server HTML stays on the final frame. The hold is armed only after this
 * effect finds the wrapper outside the viewport, so a missing script cannot
 * leave content on the animation's first frame. Callers keep the animation on
 * `[data-entrance-armed]` and flip `animation-play-state` to running under
 * `[data-entrance-armed][data-entered]`. Dropping the animation when
 * `data-entered` appears cancels it before it plays.
 *
 * The attributes are written on the DOM node instead of held in state: they
 * only gate CSS, and React never renders them, so a later render cannot clear
 * them. A wrapper that is already visible, or a browser without
 * IntersectionObserver, is marked entered without arming.
 */
function EntranceOnView({ children, className }: EntranceOnViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (
      typeof IntersectionObserver === "undefined" ||
      isInViewport(container)
    ) {
      container.dataset.entered = "";
      return;
    }

    container.dataset.entranceArmed = "";

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        container.dataset.entered = "";
        observer.disconnect();
      }
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className={className} ref={containerRef}>
      {children}
    </div>
  );
}

export { EntranceOnView };
